/**
 * FavoritesTab Component - Display and manage favorite properties
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useFavorites } from '../../agents/favoritesAgent';
import APIAgent from '../../agents/apiAgent';
import { getTopFeatureHighlights } from '../../utils/propertyFeatures';
import './FavoritesTab.css';
import { isVacantLand, VACANT_LAND_LABEL } from '../../utils/propertyKind';

const PAGE_SIZE = 6;
const PAGE_SIZE_OPTIONS = [6, 12, 24];
const HOUSE_LOAD_MAX_ATTEMPTS = 3;
const HOUSE_LOAD_RETRY_BASE_MS = 300;

const wait = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const FavoritesTab = ({ buyerId, onViewHouse }) => {
  const { favorites, loading, error, fetchFavorites, removeFavorite } = useFavorites(buyerId);
  const [housesById, setHousesById] = useState(new Map());
  const [pendingRemove, setPendingRemove] = useState(null);
  const [removingHouseIds, setRemovingHouseIds] = useState(new Set());
  const [removeNotice, setRemoveNotice] = useState(null);
  // Track which house IDs we have a definitive answer for. Without this,
  // the brief window between favorites arriving and houses arriving causes
  // every card to render the "Listing unavailable" fallback.
  const [resolvedHouseIds, setResolvedHouseIds] = useState(new Set());
  // House IDs the backend confirmed do not exist (HTTP 404). Only these
  // should display the "removed from listing" fallback.
  const [missingHouseIds, setMissingHouseIds] = useState(new Set());

  useEffect(() => {
    fetchFavorites();
  }, [buyerId, fetchFavorites]);

  useEffect(() => {
    if (!removeNotice) return undefined;
    const timer = setTimeout(() => setRemoveNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [removeNotice]);

  useEffect(() => {
    if (!pendingRemove) return;
    const stillExists = favorites.some((fav) => fav.id === pendingRemove.favoriteId);
    if (!stillExists) {
      setPendingRemove(null);
    }
  }, [favorites, pendingRemove]);

  // Fetch each favorited house directly to avoid pagination gaps from /api/houses
  useEffect(() => {
    let cancelled = false;

    const loadHouseWithRetry = async (houseId) => {
      for (let attempt = 1; attempt <= HOUSE_LOAD_MAX_ATTEMPTS; attempt += 1) {
        try {
          const house = await APIAgent.request(`/api/houses/${houseId}`, 'GET');
          return { houseId, house, missing: false, resolved: true };
        } catch (err) {
          // Only HTTP 404 means the listing was actually removed.
          const status =
            (err && (err.status || err.statusCode)) ||
            (err && err.message && /\b404\b/.test(err.message) ? 404 : null);
          if (status === 404) {
            return { houseId, house: null, missing: true, resolved: true };
          }

          const isLastAttempt = attempt === HOUSE_LOAD_MAX_ATTEMPTS;
          if (isLastAttempt) {
            console.warn(`Failed to load favorited house ${houseId} after ${attempt} attempts:`, err);
            return { houseId, house: null, missing: false, resolved: true };
          }

          await wait(HOUSE_LOAD_RETRY_BASE_MS * attempt);
        }
      }

      return { houseId, house: null, missing: false, resolved: true };
    };

    const loadFavoritedHouses = async () => {
      const initialHouses = new Map();
      const initialResolved = new Set();
      const initialMissing = new Set();
      const houseIdsToLookup = new Set();

      favorites.forEach((fav) => {
        const houseId = fav?.house_id;
        if (!houseId) return;

        // New API payload returns favorite.house directly.
        const hasHouseField = Object.prototype.hasOwnProperty.call(fav, 'house');
        if (hasHouseField) {
          initialResolved.add(houseId);
          if (fav.house) {
            initialHouses.set(houseId, fav.house);
          } else {
            initialMissing.add(houseId);
          }
          return;
        }

        // Backward compatibility: older payloads might include flattened house data.
        if (fav.address) {
          initialResolved.add(houseId);
          initialHouses.set(houseId, {
            id: houseId,
            address: fav.address,
            city: fav.city,
            state: fav.state,
            zip_code: fav.zip_code,
            price: fav.price,
            bedrooms: fav.bedrooms,
            bathrooms: fav.bathrooms,
            square_feet: fav.square_feet,
            image_urls: fav.image_urls,
            mls_profile: fav.mls_profile,
          });
          return;
        }

        // Legacy API: fetch house details separately only when not included.
        houseIdsToLookup.add(houseId);
      });

      if (favorites.length === 0) {
        setHousesById(new Map());
        setResolvedHouseIds(new Set());
        setMissingHouseIds(new Set());
        return;
      }

      if (houseIdsToLookup.size === 0) {
        setHousesById(initialHouses);
        setResolvedHouseIds(initialResolved);
        setMissingHouseIds(initialMissing);
        return;
      }

      const results = await Promise.all(
        [...houseIdsToLookup].map((houseId) => loadHouseWithRetry(houseId))
      );

      if (cancelled) return;

      setHousesById(() => {
        const next = new Map(initialHouses);
        results.forEach(({ houseId, house }) => {
          if (house) next.set(houseId, house);
        });
        return next;
      });
      setResolvedHouseIds(() => {
        const next = new Set(initialResolved);
        // Mark as resolved when retries have either loaded the house,
        // confirmed it missing, or exhausted retry attempts.
        results.forEach(({ houseId, resolved }) => {
          if (resolved) next.add(houseId);
        });
        return next;
      });
      setMissingHouseIds(() => {
        const next = new Set(initialMissing);
        results.forEach(({ houseId, missing }) => {
          if (missing) next.add(houseId);
        });
        return next;
      });
    };

    loadFavoritedHouses();

    return () => {
      cancelled = true;
    };
  }, [favorites]);

  const displayFavorites = useMemo(
    () =>
      favorites.map((fav) => {
        const resolvedHouse = housesById.get(fav.house_id);

        // Backward compatibility: some clients/tests provide house details directly on favorite rows
        const fallbackHouse = fav.address
          ? {
              id: fav.house_id,
              address: fav.address,
              city: fav.city,
              state: fav.state,
              zip_code: fav.zip_code,
              price: fav.price,
              bedrooms: fav.bedrooms,
              bathrooms: fav.bathrooms,
              square_feet: fav.square_feet,
              image_urls: fav.image_urls,
              mls_profile: fav.mls_profile,
            }
          : null;

        const house = resolvedHouse || fallbackHouse;
        const houseLoaded = resolvedHouseIds.has(fav.house_id) || Boolean(fallbackHouse);
        const houseMissing = missingHouseIds.has(fav.house_id) && !fallbackHouse;

        return { ...fav, house, houseLoaded, houseMissing };
      }),
    [favorites, housesById, resolvedHouseIds, missingHouseIds]
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const totalPages = Math.max(1, Math.ceil(displayFavorites.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const pageEnd = pageStart + pageSize;
  const paginatedFavorites = displayFavorites.slice(pageStart, pageEnd);

  const pageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, 'ellipsis-right', totalPages];
    }

    if (currentPage >= totalPages - 2) {
      return [1, 'ellipsis-left', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, 'ellipsis-left', currentPage - 1, currentPage, currentPage + 1, 'ellipsis-right', totalPages];
  }, [currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [buyerId]);

  useEffect(() => {
    setCurrentPage((prevPage) => Math.min(prevPage, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  const requestRemove = (favorite) => {
    if (!favorite) return;
    const label = favorite.house?.address || favorite.address || `House #${favorite.house_id}`;
    setPendingRemove({
      favoriteId: favorite.id,
      houseId: favorite.house_id,
      label,
    });
  };

  const cancelRemove = () => {
    setPendingRemove(null);
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.houseId) return;

    const { houseId, label } = pendingRemove;
    setRemovingHouseIds((prev) => {
      const next = new Set(prev);
      next.add(houseId);
      return next;
    });

    try {
      await removeFavorite(houseId);
      setRemoveNotice({ kind: 'success', text: `${label} removed from favorites.` });
    } catch (err) {
      setRemoveNotice({ kind: 'error', text: 'Could not remove favorite. Please try again.' });
    } finally {
      setRemovingHouseIds((prev) => {
        const next = new Set(prev);
        next.delete(houseId);
        return next;
      });
      setPendingRemove(null);
    }
  };

  const handleViewHouse = (houseId) => {
    if (typeof onViewHouse === 'function') {
      onViewHouse(houseId);
    }
  };

  if (loading) {
    return <div className="favorites-tab loading">Loading favorites...</div>;
  }

  return (
    <div className="favorites-tab">
      <div className="favorites-header">
        <h2>My Favorites</h2>
        <span className="count">{displayFavorites.length}</span>
      </div>

      {error && <div className="error-message">{error}</div>}
      {removeNotice && (
        <div
          className={`favorites-feedback ${removeNotice.kind === 'error' ? 'is-error' : 'is-success'}`}
          role="status"
          aria-live="polite"
        >
          {removeNotice.text}
        </div>
      )}

      {displayFavorites.length === 0 ? (
        <div className="empty-state">
          <p>No favorites yet. Add houses to your favorites to see them here!</p>
        </div>
      ) : (
        <>
          <div className="favorites-pagination-toolbar">
            <div className="favorites-pagination-info" aria-live="polite">
              Showing {pageStart + 1}–{Math.min(pageEnd, displayFavorites.length)} of {displayFavorites.length}
            </div>
            <label className="favorites-page-size" htmlFor="favorites-page-size">
              Per page
              <select
                id="favorites-page-size"
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((sizeOption) => (
                  <option key={sizeOption} value={sizeOption}>
                    {sizeOption}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="favorites-list">
          {paginatedFavorites.map((favorite) => (
            <div key={favorite.id} className="favorite-card">
              {(() => {
                const isPendingRemove = pendingRemove?.favoriteId === favorite.id;
                const isRemoving = removingHouseIds.has(favorite.house_id);
                return (
                  <>
              {favorite.house && (
                <>
                  {(() => {
                    const topFeatures = getTopFeatureHighlights(favorite.house.mls_profile, 4);
                    return (
                      <>
                  <div className="card-image">
                    {favorite.house.image_urls && favorite.house.image_urls[0] ? (
                      <img
                        src={APIAgent.getImageUrl(favorite.house.image_urls[0])}
                        alt={favorite.house.address}
                      />
                    ) : (
                      <div className="card-image-placeholder">
                        <span>🏠</span>
                        <p>No photo available</p>
                      </div>
                    )}
                  </div>
                  <div className="card-content">
                    <h3>{favorite.house.address}</h3>
                    <p className="location">
                      {favorite.house.city}, {favorite.house.state}{' '}
                      {favorite.house.zip_code}
                    </p>
                    <div className="card-details">
                      <span className="price">${favorite.house.price?.toLocaleString()}</span>
                      {isVacantLand(favorite.house) ? (
                        <span className="land">{VACANT_LAND_LABEL}</span>
                      ) : (
                        <>
                          <span className="bedrooms">{favorite.house.bedrooms} bed</span>
                          <span className="bathrooms">{favorite.house.bathrooms} bath</span>
                          <span className="sqft">{favorite.house.square_feet?.toLocaleString()} sqft</span>
                        </>
                      )}
                    </div>
                    {topFeatures.length > 0 && (
                      <div className="favorite-feature-pills" aria-label="Top property features">
                        {topFeatures.map((feature) => (
                          <span key={`${favorite.id}-${feature}`} className="favorite-feature-pill">
                            {feature}
                          </span>
                        ))}
                      </div>
                    )}
                    {favorite.notes && (
                      <div className="notes">{favorite.notes}</div>
                    )}
                    <button
                      className="btn-view-house"
                      onClick={() => handleViewHouse(favorite.house_id)}
                    >
                      View House
                    </button>
                    <button
                      className="btn-remove"
                      onClick={() => requestRemove(favorite)}
                      disabled={isRemoving}
                    >
                      {isRemoving ? 'Removing…' : isPendingRemove ? 'Confirm remove…' : 'Remove'}
                    </button>
                  </div>
                      </>
                    );
                  })()}
                </>
              )}

              {!favorite.house && !favorite.houseLoaded && (
                <div className="card-content card-loading" aria-live="polite">
                  <div className="card-skeleton-line wide" />
                  <div className="card-skeleton-line" />
                  <div className="card-skeleton-line short" />
                  <p className="card-loading-label">Loading property details…</p>
                </div>
              )}

              {!favorite.house && favorite.houseLoaded && favorite.houseMissing && (
                <div className="card-content">
                  <h3>Listing unavailable</h3>
                  <p className="location">
                    This property has been removed from the listings.
                  </p>
                  {favorite.notes && <div className="notes">{favorite.notes}</div>}
                  <button
                    className="btn-remove"
                    onClick={() => requestRemove(favorite)}
                    disabled={isRemoving}
                  >
                    {isRemoving ? 'Removing…' : isPendingRemove ? 'Confirm remove…' : 'Remove'}
                  </button>
                </div>
              )}

              {!favorite.house && favorite.houseLoaded && !favorite.houseMissing && (
                <div className="card-content">
                  <h3>Could not load property</h3>
                  <p className="location">
                    There was a problem loading this listing. Please try again.
                  </p>
                  {favorite.notes && <div className="notes">{favorite.notes}</div>}
                  <button
                    className="btn-view-house"
                    onClick={() => fetchFavorites()}
                  >
                    Retry
                  </button>
                  <button
                    className="btn-remove"
                    onClick={() => requestRemove(favorite)}
                    disabled={isRemoving}
                  >
                    {isRemoving ? 'Removing…' : isPendingRemove ? 'Confirm remove…' : 'Remove'}
                  </button>
                </div>
              )}

              {isPendingRemove && (
                <div className="favorite-remove-confirm" role="alertdialog" aria-label="Confirm remove favorite">
                  <p>Remove <strong>{pendingRemove.label}</strong> from your favorites?</p>
                  <div className="favorite-remove-confirm__actions">
                    <button
                      className="btn-remove-confirm"
                      onClick={confirmRemove}
                      disabled={isRemoving}
                    >
                      {isRemoving ? 'Removing…' : 'Yes, remove'}
                    </button>
                    <button
                      className="btn-remove-cancel"
                      onClick={cancelRemove}
                      disabled={isRemoving}
                    >
                      Keep it
                    </button>
                  </div>
                </div>
              )}
                  </>
                );
              })()}
            </div>
          ))}
          </div>
          {totalPages > 1 && (
            <div className="favorites-pagination" role="navigation" aria-label="Favorites pagination">
              <button
                className="pagination-button"
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage <= 1}
              >
                Previous
              </button>
              <div className="pagination-pages" aria-label="Page numbers">
                {pageItems.map((item) => {
                  if (typeof item !== 'number') {
                    return (
                      <span key={item} className="pagination-ellipsis" aria-hidden="true">
                        …
                      </span>
                    );
                  }

                  return (
                    <button
                      key={item}
                      className={`pagination-page-button ${item === currentPage ? 'active' : ''}`}
                      type="button"
                      onClick={() => setCurrentPage(item)}
                      aria-current={item === currentPage ? 'page' : undefined}
                      aria-label={`Go to page ${item}`}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
              <span className="pagination-status">Page {currentPage} of {totalPages}</span>
              <button
                className="pagination-button"
                type="button"
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FavoritesTab;
